/**
 * Product Catalog Utilities
 * Client-side filtering and sorting for product catalog display
 *
 * Note: These are different from server-side query filters.
 * They operate on already-fetched product data for instant UI filtering.
 */

import type { PricedProduct, Category } from '../types'

/**
 * Filter products to only active ones
 */
export function filterActive(products: PricedProduct[]): PricedProduct[] {
  return products.filter((product) => product.isActive)
}

/**
 * Filter products that have at least one variant with a price
 */
export function filterWithPrices(products: PricedProduct[]): PricedProduct[] {
  return products.filter((product) =>
    product.variants.some((variant) => variant.unitPrice !== null && variant.unitPrice > 0)
  )
}

/**
 * Filter products by category code
 */
export function filterByCategory(products: PricedProduct[], categoryCode: string): PricedProduct[] {
  if (!categoryCode || categoryCode === 'all') return products
  return products.filter((product) =>
    product.categories.some((category) => category.code === categoryCode)
  )
}

/**
 * Filter products by search term (searches name in both languages and code)
 */
export function filterBySearch(products: PricedProduct[], searchTerm: string): PricedProduct[] {
  if (!searchTerm || searchTerm.trim() === '') return products

  const term = searchTerm.toLowerCase().trim()

  return products.filter((product) => {
    const nameEn = product.name?.en?.toLowerCase() ?? ''
    const nameAr = product.name?.ar?.toLowerCase() ?? ''
    const code = product.code?.toLowerCase() ?? ''

    return nameEn.includes(term) || nameAr.includes(term) || code.includes(term)
  })
}

/**
 * Sort products by name
 */
export function sortByName(
  products: PricedProduct[],
  lang: 'en' | 'ar' = 'en'
): PricedProduct[] {
  return [...products].sort((a, b) => {
    const nameA = (lang === 'ar' ? a.name?.ar : a.name?.en) ?? ''
    const nameB = (lang === 'ar' ? b.name?.ar : b.name?.en) ?? ''
    return nameA.localeCompare(nameB, lang)
  })
}

/**
 * Sort products by price (using first variant's price)
 */
export function sortByPrice(
  products: PricedProduct[],
  order: 'asc' | 'desc' = 'asc'
): PricedProduct[] {
  return [...products].sort((a, b) => {
    const priceA = getLowestPrice(a)
    const priceB = getLowestPrice(b)
    return order === 'asc' ? priceA - priceB : priceB - priceA
  })
}

/**
 * Get lowest price from product variants
 */
function getLowestPrice(product: PricedProduct): number {
  const prices = product.variants
    .map((v) => v.unitPrice)
    .filter((p): p is number => p !== null && p > 0)

  return prices.length > 0 ? Math.min(...prices) : Infinity
}

/**
 * Get unique categories from products
 */
export function getUniqueCategories(products: PricedProduct[]): Category[] {
  const categoryMap = new Map<string, Category>()

  for (const product of products) {
    for (const category of product.categories) {
      if (!categoryMap.has(category.documentId)) {
        categoryMap.set(category.documentId, category)
      }
    }
  }

  return Array.from(categoryMap.values()).sort((a, b) => {
    const nameA = a.name?.en ?? ''
    const nameB = b.name?.en ?? ''
    return nameA.localeCompare(nameB)
  })
}
